import { MetaDataMap } from '@app/shared/components/filter-input/meta-data-map';
import { FilterableEntry } from '@app/shared/models/filterable-entry';
import { toMap } from '@app/utils/common';
import { filterExclusive } from '@app/utils/filter/filter';
import { isPositiveParse } from '@app/utils/filter/parser/parse-result';
import { parse } from '@app/utils/filter/parser/parser';
import { ParserError } from '@app/utils/filter/parser/parser-error';
import { simpleParse } from '@app/utils/filter/parser/simple-parser';
import { foolproofTerm } from '@app/utils/filter/terms/foolproof-term';
import { termToString } from '@app/utils/filter/terms/term-to-string';
import { MetaData } from '@app/utils/lists/meta-list/meta-data';
import { MetaDataProperty } from '@app/utils/lists/meta-list/meta-data-property';
import { MetaList } from '@app/utils/lists/meta-list/meta-list';
import { MetaListEntry } from '@app/utils/lists/meta-list/meta-list-entry';
import { MetaListCountMode, MetaListOptions } from '@app/utils/lists/meta-list/meta-list-types';
import { loggerFor } from '@app/utils/logger';
import { isBlank, isEmpty, isNotBlank } from '@app/utils/string';
import { StringMap } from '@app/utils/string-map';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';

export class MetaListImpl<T> implements MetaList<T> {
  public properties: MetaDataProperty[];

  private _error$: Subject<ParserError | null> = new Subject();
  private _filter$: BehaviorSubject<string> = new BehaviorSubject('');
  private _items$: BehaviorSubject<T[]> = new BehaviorSubject([]);
  private _properties$: BehaviorSubject<MetaDataProperty[]> = new BehaviorSubject([]);

  private calculateMetaData: (item: T) => FilterableEntry;
  private countType: MetaListCountMode;
  private entries: T[];

  /**
   * A map of "entry key": { active: boolean, count: number }
   */
  private entryMap: Map<string, MetaListEntry<T>> = new Map();

  private itemsWithMetaData: MetaData<T>[];
  private logger = loggerFor('MetaList');
  private propertyMap: StringMap<MetaDataProperty> = {};
  private term: string = '';

  private originalProperties: MetaDataProperty[];

  public get error$(): Observable<ParserError | null> {
    return this._error$;
  }

  public get filter$(): Observable<string> {
    return this._filter$;
  }

  public get items$(): Observable<T[]> {
    return this._items$;
  }

  public get properties$(): Observable<MetaDataProperty[]> {
    return this._properties$;
  }

  constructor(
    private translateService: TranslateService,
    options: MetaListOptions<T>
  ) {
    this.calculateMetaData = options.calculateMetaData;
    this.countType = options.countMode;
  }

  public clearFilter(): this {
    this.term = '';

    this.resetEntryMap();
    this.updateActiveCounters();

    return this;
  }

  deactivate(key: string): this {
    if (this.entryMap.has(key)) {
      this.entryMap.get(key).active = false;

      this.updateActiveCounters();
    } else {
      this.logger.warn(`No known entry with '${key}'`);
    }

    return this;
  }

  /**
   * Apply the current filter and emit the filter and the filtered items
   */
  public draw(): this {
    const { filter, filteredItems, items } = this.filter();

    this.updateCounters(items);

    this._filter$.next(filter);
    this._items$.next(filteredItems);

    this.logger.debug('entry map', this.entryMap);
    this.logger.debug('filtered items', filteredItems);

    return this;
  }

  public getActiveProperties(): Record<string, string[]> | null {
    let activeProperties: Record<string, string[]> | null = null;

    if (this.properties != null) {
      activeProperties = {};

      this.properties.forEach((property) => {
        const activeValues = property.values.filter((value) => this.isKeyActive(`${property.key}:${value}`));

        if (activeValues.length > 0) {
          activeProperties[property.key] = activeValues;
        }
      });
    }

    return activeProperties;
  }

  public getEntry(key: string): MetaListEntry<T> | null {
    return this.entryMap?.get(key) ?? null;
  }

  /**
   * Returns true if any filter property is active
   */
  public isActive(): boolean {
    let isActive: boolean = false;

    if (this.entryMap != null) {
      for (const [, value] of this.entryMap) {
        isActive = isActive || value.active;
      }
    }

    return isActive;
  }

  public isKeyActive(key: string): boolean {
    return this.entryMap.get(key)?.active ?? false;
  }

  public keyCount(key: string): number {
    return this.entryMap.get(key)?.count ?? 0;
  }

  public refresh(): this {
    this.updateProperties(this.originalProperties);
    this.updateFilter(this.term);

    this.createEntryMap(this.properties);

    return this;
  }

  /**
   * Toggle the active state of the given property
   * @param key Key that identifies the property
   */
  public toggle(key: string): this {
    const propertyKey: string = key.split(':')[0];
    const property = this.propertyMap[propertyKey];

    if (property != null) {
      if (property.type === 'single') {
        const entry = this.entryMap.get(key);
        const isActive: boolean = entry?.active === true;

        // Mark all values as inactive
        for (const value of property.values) {
          const metaKey = `${property.key}:${value}`;

          if (this.entryMap.has(metaKey)) {
            const entry = this.entryMap.get(metaKey);

            entry.active = false;
          }
        }

        if (this.entryMap.has(key)) {
          this.entryMap.get(key).active = !isActive;
        }
      } else {
        if (this.entryMap.has(key)) {
          const entry = this.entryMap.get(key);

          entry.active = !entry.active;
        }
      }

      this.updateActiveCounters();
    }

    return this;
  }

  /**
   * Updates the entries and calculates the meta data for each entry (if the properties are set)
   * @param entries
   */
  public updateEntries(entries?: T[]): this {
    this.logger.trace('Update entries', entries);

    if (entries != null) {
      this.entries = entries;

      if (this.properties != null) {
        this.updateItemsWithMetaData(entries);
      }
    }

    return this;
  }

  /**
   * Updates the filter value
   */
  public updateFilter(term: string): this {
    this.logger.trace('Update filter', term);

    this.term = simpleParse(term).map(foolproofTerm).join(' ');

    this.logger.debug('Update filter term value to', this.term);

    this.updateActiveCounters();

    return this;
  }

  /**
   * Updates properties and recalculates the meta data for the entries (if they are set)
   * @param properties
   */
  public updateProperties(properties?: MetaDataProperty[]): this {
    this.logger.trace('Update properties', properties);

    if (properties != null) {
      this.originalProperties = properties;

      this.properties = this.translateProperties(properties);
      this.propertyMap = toMap(this.properties, 'key');

      const entryMap = this.createEntryMap(this.properties);

      this.entryMap.forEach((value, key) => {
        const v = entryMap.get(key);
        if (v != null) {
          v.active = value.active;
        }
      });

      this.entryMap = entryMap;

      if (this.entries != null) {
        this.updateItemsWithMetaData(this.entries);
      }

      this._properties$.next(this.properties);
    }

    return this;
  }

  /**
   * Calculate the current filter value based on the term and active properties
   */
  private calculateFilter(): string {
    let newFilter: string;

    const filter: string[] = [];

    for (const property of this.properties) {
      let term = this.calculateFilterForProperty(property);

      if (isNotBlank(term)) {
        filter.push(term);
      }
    }

    if (isBlank(this.term)) {
      newFilter = filter.join(' ');
    } else {
      const parsedQuery = parse(this.term);

      if (isPositiveParse(parsedQuery)) {
        // filter only for typed in filters
        const typedInFilters = parsedQuery.terms.filter((el) => el.key === 'key');
        for (const term of typedInFilters) {
          filter.push(termToString(term));
        }

        newFilter = filter.join(' ');
      } else {
        newFilter = this.term;
      }
    }

    this.term = newFilter;

    this.logger.debug('Calculated new filter', newFilter);

    return newFilter;
  }

  private calculateFilterForProperty(property: MetaDataProperty) {
    let term: string = '';

    if (property.type === 'multi-and') {
      term = this.calculateMultiAndFilter(property);
    } else if (property.type === 'multi-or') {
      term = this.calculateMultiOrFilter(property);
    } else if (property.type === 'single') {
      term = this.calculateSingleFilter(property);
    }
    return term;
  }

  private calculateMultiAndFilter(property: MetaDataProperty): string {
    let term: string = '';
    let terms: string[] = [];

    for (const value of property.values) {
      const key = `${property.key}:${value}`;

      if (this.entryMap.has(key) && this.entryMap.get(key).active === true) {
        terms.push(`${property.key}:${foolproofTerm(value)}`);
      }
    }

    if (terms.length > 0) {
      term = terms.join(' ');
    }
    return term;
  }

  private calculateMultiOrFilter(property: MetaDataProperty): string {
    let term: string = '';
    let terms: string[] = [];

    for (const value of property.values) {
      const key = `${property.key}:${value}`;

      if (this.entryMap.has(key) && this.entryMap.get(key).active === true) {
        terms.push(foolproofTerm(value));
      }
    }

    if (terms.length > 0) {
      term = `${property.key}:${terms.join(',')}`;
    }

    return term;
  }

  private calculateSingleFilter(property: MetaDataProperty) {
    return this.calculateMultiAndFilter(property);
  }

  private createEntryMap(properties: MetaDataProperty[]): Map<string, MetaListEntry<T>> {
    const map: Map<string, MetaListEntry<T>> = new Map();

    // Populate the active entries map
    properties.forEach((property) => {
      property.values.forEach((value) => {
        const metaKey = `${property.key}:${value}`;

        map.set(metaKey, {
          active: false,
          count: 0,
          key: property.key,
          label: value,
          value: metaKey,
        });
      });
    });

    return map;
  }

  private filter() {
    const filter = this.calculateFilter();

    const { error, values } = filterExclusive(this.itemsWithMetaData, filter.trim());
    const filteredItems = values.map((item) => item.item);

    this._error$.next(error || null);

    return {
      filter,
      filteredItems,
      items: values,
    };
  }

  private flattenMetaData(def?: StringMap<string | string[]>): MetaDataMap {
    const meta = new MetaDataMap();

    if (def !== undefined) {
      Object.entries(def).forEach(([key, value]: [string, string | string[] | undefined]) => {
        if (typeof value === 'string') {
          const translatedValue = isEmpty(value) ? value : this.translateService.instant(value);

          meta.set(key, [translatedValue]);
        } else if (value != null) {
          meta.set(
            key,
            value.map((_) => (isEmpty(_) ? _ : this.translateService.instant(_)))
          );
        }
      });
    }

    return meta;
  }

  private getMetaData(item: T): MetaData<T> {
    let filterableEntry = this.calculateMetaData(item);

    const metaData: MetaDataMap = this.flattenMetaData(filterableEntry.meta);

    metaData.set('key', [filterableEntry.key]);

    return {
      item: item,
      meta: metaData,
    };
  }

  private resetCounters(): void {
    for (const [, value] of this.entryMap) {
      value.count = 0;
    }
  }

  private resetEntryMap() {
    // Reset all entries
    for (const [, value] of this.entryMap) {
      value.active = false;
    }
  }

  private translateProperties(properties: MetaDataProperty[]): MetaDataProperty[] {
    // Translate values of each entry
    const ps = properties.map((entry) => {
      return {
        ...entry,
        activeFilters: 0,
        values: entry.values.map((value) => (value != undefined ? this.translateService.instant(value) : '')),
      };
    });

    return ps;
  }

  private updateActiveCounters(): void {
    for (const property of this.properties) {
      property.activeFilters = 0;

      for (const value of property.values) {
        const key = `${property.key}:${value}`;

        if (this.entryMap.get(key)?.active === true) {
          ++property.activeFilters;
        }
      }
    }
  }

  private updateCounters(items: MetaData<T>[]): void {
    if (this.countType === MetaListCountMode.Dynamic) {
      for (const [, value] of this.entryMap) {
        value.count = items.filter((item) => item.meta.get(value.key).includes(value.label)).length;
      }
    }
  }

  private updateItemsWithMetaData(entries: T[]): void {
    this.resetCounters();

    this.itemsWithMetaData = entries.map((entry) => {
      const metaData: MetaData<T> = this.getMetaData(entry);

      for (const key of metaData.meta.iterator()) {
        const entry = this.entryMap.get(key);

        if (entry != null) {
          ++entry.count;
        }
      }

      return metaData;
    });
  }
}
